import { Textarea } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { IEventValidationModel } from '../../models/events/IEventFormValidation';
import FieldElemContainer from './field-element-container/FieldElementContainer';
import './SchemaGenerator.scss';
import { Field, TypeNode, Utils } from './Utils';

interface EventWrapperProps {
  isCheckClicked?: boolean
  onAvroConfirmClickHandler: (value: string, encryptFieldExist: boolean) => void;
  isConfirmClick: boolean,
  formData: IEventValidationModel,
  setSensitiveFieldsArr: (sensitiveFields: string[]) => void;
  sensitiveFieldsArr: string[],
  disableAvroschemaConfirm: (disable: boolean) => void;
}

const SchemaGeneratorWrapper = ({ isCheckClicked = false, onAvroConfirmClickHandler, isConfirmClick, formData, setSensitiveFieldsArr, sensitiveFieldsArr, disableAvroschemaConfirm }: EventWrapperProps) => {
  const [avroSchema, setAvroSchema] = useState('');
  const [encryptDataArr, setEncryptDataArr] = useState<string[]>([]);
  const { t: translate } = useTranslation();
  const [isSensitiveFieldExist, setIsSensitiveFieldExist] = useState(false);
  const [fields, setFields] = useState<Field[]>([
    {
      id: uuid(),
      namespace: '',
      name: '',
      type: 'record',
    }
  ]);

  /**
   * @method normalizeArrayItems
   * @description Normalizes array items to ensure only one of 'int' or 'double' is present.
   * If both are present, keeps 'double' and removes 'int'.
   * @param field - The field to normalize.
   * @returns The normalized field.
   */
  const normalizeArrayItems = (field: Field): Field => {
    const normalizedField = { ...field };

    // Normalize array items if field has items
    if (normalizedField.items && Array.isArray(normalizedField.items)) {
      const hasInt = normalizedField.items.some((item: string | TypeNode) => item === 'int' || (typeof item === 'object' && item.type === 'int'));
      const hasDouble = normalizedField.items.some((item: string | TypeNode) => item === 'double' || (typeof item === 'object' && item.type === 'double'));

      if (hasInt && hasDouble) {
        // Remove 'int' and keep 'double'
        normalizedField.items = normalizedField.items.filter((item: string | TypeNode) => {
          if (item === 'int') return false;
          if (typeof item === 'object' && item.type === 'int') return false;
          return true;
        });
      }
    }

    // Normalize array type's items if field type is an array
    if (Array.isArray(normalizedField.type)) {
      normalizedField.type = normalizedField.type.map((typeItem: string | TypeNode) => {
        if (typeof typeItem === 'object' && typeItem.type === 'array' && typeItem.items && Array.isArray(typeItem.items)) {
          const hasInt = typeItem.items.some((item: string | TypeNode) => item === 'int' || (typeof item === 'object' && item.type === 'int'));
          const hasDouble = typeItem.items.some((item: string | TypeNode) => item === 'double' || (typeof item === 'object' && item.type === 'double'));

          if (hasInt && hasDouble) {
            // Remove 'int' and keep 'double'
            return {
              ...typeItem,
              items: typeItem.items.filter((item: string | TypeNode) => {
                if (item === 'int') return false;
                if (typeof item === 'object' && item.type === 'int') return false;
                return true;
              })
            };
          }
        }
        return typeItem;
      });
    } else if (typeof normalizedField.type === 'object' && normalizedField.type.type === 'array' && normalizedField.type.items && Array.isArray(normalizedField.type.items)) {
      // Handle case where field.type is a single TypeNode object with type='array'
      const hasInt = normalizedField.type.items.some((item: string | TypeNode) => item === 'int' || (typeof item === 'object' && item.type === 'int'));
      const hasDouble = normalizedField.type.items.some((item: string | TypeNode) => item === 'double' || (typeof item === 'object' && item.type === 'double'));

      if (hasInt && hasDouble) {
        // Remove 'int' and keep 'double'
        normalizedField.type = {
          ...normalizedField.type,
          items: normalizedField.type.items.filter((item: string | TypeNode) => {
            if (item === 'int') return false;
            if (typeof item === 'object' && item.type === 'int') return false;
            return true;
          })
        };
      }
    }

    // Recursively normalize child fields
    if (normalizedField.fields && Array.isArray(normalizedField.fields)) {
      normalizedField.fields = normalizedField.fields.map((childField: Field) => normalizeArrayItems(childField));
    }

    return normalizedField;
  };

  /**
   * @method updateFields
   * @description Updates the fields and sets the Avro schema.
   * @param fields - The array of fields to update.
   */
  const updateFields = (fields: Field[]) => {
    // Normalize fields to ensure only one of 'int' or 'double' in array items
    const normalizedFields = fields.map((field) => normalizeArrayItems(field));
    setFields(normalizedFields);
    const jsonData = JSON.stringify(normalizedFields[0], ((k, v) => ((k === 'id') ? undefined : v)), 4);
    setAvroSchema(jsonData);
  };

  /**
   * @method updateEncryptedArr
   * @description Updates the encrypted array based on the provided ID and checked value.
   * @param id - The ID of the item to update in the array.
   * @param checked - The boolean value indicating whether the item should be added or removed from the array.
   */
  const updateEncryptedArr = (id: string, checked: boolean) => {
    setEncryptDataArr((preVal) => {
      if (!checked) {
        const newEncryptedVal = preVal.filter((item) => item !== id);
        return newEncryptedVal;
      }
      return [...preVal, id];
    });
  };

  /**
   * @method recordTypeCheck
   * @description Checks if the field type is 'record' and if it has child fields.
   * Renders the child fields if they exist.
   * @param field - The field to check and render child fields for.
   * @returns The rendered child fields if they exist, otherwise null.
   */
  const recordTypeCheck = (field: Field) => (field.type === 'record' && field.fields && field.fields.length > 0
    && <div className="child-field">{field.fields?.map((property) => { property.id = property.id ?? uuid(); return renderFields(property, field); })}</div>
  );

  /**
   * @method arrayOrRecordTypeCheck
   * @description Checks if the field is an array or record type and renders the corresponding fields.
   * @param field The field to check.
   * @returns The rendered fields if the field is an array or record type, otherwise an empty string.
   */
  const arrayOrRecordTypeCheck = (field: Field) => (field.type as Array<TypeNode>).map((nodeType) => {
    if (typeof nodeType === 'object') {
      if (nodeType?.type && nodeType.type === 'record' && nodeType.fields) {
        return (<div key={`${field.id}-record`} className="child-field">{nodeType.fields?.map((property) => { property.id = property.id ?? uuid(); return renderFields(property, field); })}</div>);
      }
    }
    return '';
  });

  /**
   * @method arrayTypeCheck
   * @description Checks if the field is of array type and renders the array field.
   * @param field - The field to check.
   * @returns The rendered array field or an empty string.
   */
  const arrayTypeCheck = (field: Field) => (field.type as Array<TypeNode>).map((nodeType) => {
    if (nodeType && nodeType.type === 'array' && nodeType.items) {
      return (renderArrayField(nodeType as Field, field));
    }
    return '';
  });

  /**
   * @method arrayTypeCheck
   * @description Returns the content to render for a given field.
   * @param field - The field object.
   * @returns The rendered content for the field.
   */
  const getRenderFieldContent = (field: Field) => {
    if (field.type === 'record') {
      return recordTypeCheck(field);
    }

    if (field.type === 'array') {
      return (renderArrayField(field, field));
    }

    if (Array.isArray(field.type) && Utils.checkTypes(field.type, 'record')) {
      return arrayOrRecordTypeCheck(field);
    }

    if (Array.isArray(field.type) && Utils.checkTypes(field.type, 'array')) {
      return arrayTypeCheck(field);
    }

    if (Utils.checkTypes(field.type, 'record')) {
      const nodeType = field as TypeNode;
      if (nodeType?.type && typeof nodeType.type === 'object' && !Array.isArray(nodeType.type) && nodeType.type.type === 'record' && nodeType.type.fields) {
        return (<div key={`${field.id}-record`} className="child-field">{nodeType.type.fields?.map((property) => { property.id = property.id ?? uuid(); return renderFields(property, field); })}</div>);
      }
    }

    if (Utils.checkTypes(field.type, 'array')) {
      const nodeType = field as TypeNode;
      if (nodeType && (nodeType.type as TypeNode).type === 'array' && (nodeType.type as TypeNode).items) {
        return (renderArrayField(nodeType.type as Field, field));
      }
    }
    return '';
  };

  /**
   * @method renderArrayField
   * @description Renders an array field.
   * @param nodeItems - The array of items to render.
   * @param field - The parent field.
   * @returns The rendered array field.
   */
  const renderArrayField = (nodeItems: Field, field: Field) => {
    if (Array.isArray(nodeItems.items)) {
      return nodeItems.items.map((item) => {
        if (typeof item === 'object') {
          item.id = item.id ?? uuid();
          return <div key={`${field.id}-array`} className="child-field">{renderFields(item as Field, field)}</div>;
        }
        return '';
      });
    }
    return '';
  };

  /**
   * @method renderFields
   * @description Renders the fields for a given field and its parent field.
   * @param field - The field to render.
   * @param parentField - The parent field of the current field.
   * @returns The rendered fields.
   */
  const renderFields = (field: Field, parentField: Field | null) => (
    <div
      key={field.id}
      className="parent-field"
      id={`field-${field.id}`}
    >
      <FieldElemContainer
        field={field}
        encryptDataArr={encryptDataArr}
        parentField={parentField}
        fields={fields}
        updateFields={updateFields}
        arrOfEncryptedData={updateEncryptedArr}
        isCheckClicked={isCheckClicked ?? false}
        disableAvroschemaConfirm={disableAvroschemaConfirm}
        isSensitiveFieldExist={isSensitiveFieldExist}
      />
      {
        getRenderFieldContent(field)
      }

    </div>
  );

  // This useEffect triggers when we have avroSchema and we click on build avro schema again
  useEffect(() => {
    if (formData.avroSchema.value && formData.avroSchema.isValid) {
      updateFields([JSON.parse(formData.avroSchema.value)]);
    } else {
      disableAvroschemaConfirm(true);
    }
    if (sensitiveFieldsArr.length) {
      setEncryptDataArr(sensitiveFieldsArr);
    }
  }, [formData.avroSchema.value, sensitiveFieldsArr]);

  // This useEffect triggers when we want to throw error message when isCheckClicked is true and zero encryptData field
  useEffect(() => {
    if (isCheckClicked && !encryptDataArr.length && avroSchema.includes('fields')) {
      setIsSensitiveFieldExist(true);
    } else {
      setIsSensitiveFieldExist(false);
    }
  }, [isCheckClicked, encryptDataArr, avroSchema]);

  useEffect(() => {
    if (isConfirmClick) {
      onAvroConfirmClickHandler(avroSchema, false);
      setSensitiveFieldsArr(encryptDataArr);
    }
  }, [isConfirmClick, isCheckClicked]);

  return (
    <div className="container-fluid avro-schema-builder">
      <div className="row">
        <div className="encrypt-schema-subtext">{isCheckClicked ? translate('schemaGeneratorText.provideAtleastOneSchemaFieldToEncrypt') : ''}</div>
        <div className="main-root col-md-8 avro-builder">
          {fields.map((field) => renderFields(field, null))}
        </div>
        <div className="col-md-4 avro-script-section">
          <span className="avro-textarea-heading">{translate('schemaGeneratorText.textareaHeader')}</span>
          <Textarea maxLength={Number.MAX_SAFE_INTEGER} name="" id="avrocontent" aria-label="build-avro-textarea" data-testid="build-avro-textarea" defaultValue={avroSchema} error={isSensitiveFieldExist} className="avro-textarea" readOnly />
          <div className="error-msg">{isSensitiveFieldExist ? translate('schemaGeneratorText.noFieldEncrypted') : ''}</div>
        </div>
      </div>
    </div>
  );
};

export default SchemaGeneratorWrapper;
